import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { GET_PUBLIC_CAMPAIGNS } from '../../graphql/queries';
import ChannelIcon from '../campaigns/ChannelIcon';
import CampaignGridCard from '../campaigns/CampaignGridCard';
import { INTEREST_CATEGORIES } from '../../constants/categories';
import { useAuthStore } from '../../store/authStore';
import GlobalNavBar from '../layout/GlobalNavBar';
import {
  CATEGORY_LABELS,
  GRADIENT_PALETTES,
  formatDate,
  getDaysRemaining,
  getProgressPercent,
  getChannels,
  getGradient,
  getCategoryLabel,
  getChannelLabel,
  formatRecruitmentPeriod,
  isRecruitmentOpen,
} from '../../utils/campaignUtils';

/* ─── Types ─── */
interface CampaignImage {
  fileKey?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
}

interface RecruitmentPeriod {
  startDate?: string;
  endDate?: string;
  alwaysRecruiting?: boolean;
}

interface Campaign {
  campaignId: string;
  campaignName?: string;
  brandName?: string;
  campaignCategory?: string;
  representativeImages?: CampaignImage[];
  contentChannels?: string[];
  contentChannel?: string;
  rewardAmount?: number;
  recruitmentPeriod?: RecruitmentPeriod;
  numberOfRecruits?: number;
  providedValueDescription?: string;
  status?: string;
  campaignType?: string;
  contentTypes?: string[];
  productName?: string;
  recommendedKeywords?: string[];
  createdAt?: string;
}

/* ─── Component-specific constants ─── */
const CHANNELS = ['instagram', 'blog', 'youtube'] as const;
const PAGE_SIZE = 9;

/* ─── Component ─── */
const PublicDashboardV2: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());
  const [recruitStatus, setRecruitStatus] = useState<'all' | 'open' | 'closed'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const fetchCampaigns = async () => {
      try {
        const response = await API.graphql({
          query: GET_PUBLIC_CAMPAIGNS,
          authMode: 'AWS_IAM',
        });
        const fetched = (response as any).data?.getPublicCampaigns?.campaigns || [];
        setCampaigns(fetched);
      } catch (err) {
        console.error('Failed to fetch campaigns:', err);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  const toggleChannel = (ch: string) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      next.has(ch) ? next.delete(ch) : next.add(ch);
      return next;
    });
  };

  const filtered = useMemo(() => {
    const result = campaigns.filter((c) => {
      // category
      if (selectedCategory && c.campaignCategory !== selectedCategory) return false;
      // search
      const q = searchQuery.toLowerCase();
      if (q) {
        const match =
          (c.campaignName || '').toLowerCase().includes(q) ||
          (c.brandName || '').toLowerCase().includes(q) ||
          (c.providedValueDescription || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      // channel filter
      if (selectedChannels.size > 0) {
        const chs = getChannels(c);
        const hasMatch = chs.some((ch) => selectedChannels.has(ch));
        if (!hasMatch) return false;
      }
      // recruit status
      if (recruitStatus !== 'all') {
        const days = c.recruitmentPeriod?.alwaysRecruiting ? 999 : getDaysRemaining(c.recruitmentPeriod?.endDate);
        if (recruitStatus === 'open' && days !== null && days < 0) return false;
        if (recruitStatus === 'closed' && (days === null || days >= 0)) return false;
      }
      return true;
    });

    // Sort: expired campaigns to bottom, rest by newest first
    const now = new Date();
    const isExpired = (c: any) => {
      if (c.recruitmentPeriod?.alwaysRecruiting) return false;
      if (!c.recruitmentPeriod?.endDate) return false;
      return new Date(c.recruitmentPeriod.endDate) < now;
    };
    result.sort((a, b) => {
      const aExp = isExpired(a) ? 1 : 0;
      const bExp = isExpired(b) ? 1 : 0;
      if (aExp !== bExp) return aExp - bExp;
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });

    return result;
  }, [campaigns, selectedCategory, searchQuery, selectedChannels, recruitStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const recommended = campaigns.slice(0, 3);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery, selectedChannels, recruitStatus]);

  /* ═══════════════════════════════════════════
     CATEGORY TABS
     ═══════════════════════════════════════════ */
  const renderCategoryTabs = () => (
    <div className="bg-white border-b border-gray-100">
      <div className="max-w-[1400px] mx-auto px-4">
        <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('')}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              !selectedCategory ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t('publicDashboard.all')}
          </button>
          {INTEREST_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                selectedCategory === cat ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t(`categories.${cat}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     LEFT SIDEBAR FILTERS
     ═══════════════════════════════════════════ */
  const renderSidebar = () => (
    <aside className="w-60 flex-shrink-0 hidden lg:block">
      <div className="sticky top-[7.5rem] space-y-6">
        {/* 채널 유형 */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('publicDashboard.channelType')}</h3>
          <div className="space-y-2">
            {CHANNELS.map((ch) => (
              <label key={ch} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedChannels.has(ch)}
                  onChange={() => toggleChannel(ch)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <ChannelIcon channel={ch} className="w-4 h-4" />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 capitalize">{ch}</span>
              </label>
            ))}
          </div>
        </div>

        {/* {t('publicDashboard.reward')} 범위 */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('publicDashboard.rewardRange')}</h3>
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600">
            $0 ~ $1,000,000
          </div>
        </div>

        {/* 모집 상태 */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('publicDashboard.recruitStatus')}</h3>
          <div className="space-y-2">
            {([['all', t('publicDashboard.recruitAll')], ['open', t('publicDashboard.recruitOpen')], ['closed', t('publicDashboard.recruitClosed')]] as [typeof recruitStatus, string][]).map(([val, label]) => (
              <label key={val} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="radio"
                  name="recruitStatus"
                  checked={recruitStatus === val}
                  onChange={() => setRecruitStatus(val)}
                  className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );

  /* ═══════════════════════════════════════════
     VIEW TOGGLE + GRID/LIST ICONS
     ═══════════════════════════════════════════ */
  const renderViewToggle = () => (
    <div className="flex items-center justify-between mb-4">
      <p className="text-sm text-gray-500">
        <span dangerouslySetInnerHTML={{ __html: t('publicDashboard.totalCampaigns', { count: filtered.length }) }} />
      </p>
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
        <button
          onClick={() => setViewMode('grid')}
          className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
          aria-label={t('publicDashboard.gridView')}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
            <rect x="1" y="1" width="6" height="6" rx="1" />
            <rect x="9" y="1" width="6" height="6" rx="1" />
            <rect x="1" y="9" width="6" height="6" rx="1" />
            <rect x="9" y="9" width="6" height="6" rx="1" />
          </svg>
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
          aria-label={t('publicDashboard.listView')}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
            <rect x="1" y="1" width="14" height="3" rx="1" />
            <rect x="1" y="6" width="14" height="3" rx="1" />
            <rect x="1" y="11" width="14" height="3" rx="1" />
          </svg>
        </button>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     LIST ROW
     ═══════════════════════════════════════════ */
  const renderListRow = (c: Campaign, idx: number) => {
    const img = c.representativeImages?.[0]?.fileUrl;
    const channels = getChannels(c);
    const days = c.recruitmentPeriod?.alwaysRecruiting ? null : getDaysRemaining(c.recruitmentPeriod?.endDate);
    const gradient = getGradient(idx);

    return (
      <div key={c.campaignId} className="flex items-center gap-4 bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md hover:border-gray-300 transition-all">
        {/* Small thumbnail */}
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
          {img ? (
            <img src={img} alt={c.campaignName || ''} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
              <span className="text-white/80 text-lg font-bold">{(c.campaignName || 'C')[0]}</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{c.campaignName || t('publicDashboard.campaign')}</h3>
          <p className="text-xs text-gray-400 truncate">{c.brandName || ''}</p>
        </div>

        {/* Channels */}
        <div className="hidden sm:flex items-center gap-1">
          {channels.map((ch, i) => (
            <ChannelIcon key={i} channel={ch} className="w-4 h-4" />
          ))}
        </div>

        {/* Reward */}
        <span className="hidden md:inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md whitespace-nowrap">
          ${(c.rewardAmount || 50000).toLocaleString()}
        </span>

        {/* Deadline */}
        <div className="hidden lg:block text-xs text-gray-400 whitespace-nowrap">
          {c.recruitmentPeriod?.alwaysRecruiting ? (
            <span className="text-emerald-500 font-medium">{t('publicDashboard.alwaysRecruiting')}</span>
          ) : days !== null && days >= 0 ? (
            <span className={days < 3 ? 'text-red-500 font-bold' : ''}>D-{days}</span>
          ) : days !== null ? (
            <span className="text-gray-300">{t('publicDashboard.closed')}</span>
          ) : null}
        </div>

        {/* Apply button */}
        <Link
          to={`/campaigns/${c.campaignId}`}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          지원하기
        </Link>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     RECOMMENDED WIDGET (right sticky)
     ═══════════════════════════════════════════ */
  const renderRecommended = () => (
    <aside className="w-64 flex-shrink-0 hidden xl:block">
      <div className="sticky top-[7.5rem]">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('publicDashboard.recommended')}</h3>
        <div className="space-y-3">
          {recommended.map((c, idx) => {
            const img = c.representativeImages?.[0]?.fileUrl;
            const gradient = getGradient(idx);
            return (
              <Link
                key={c.campaignId}
                to={`/campaigns/${c.campaignId}`}
                className="block rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all"
              >
                <div className="h-20 overflow-hidden">
                  {img ? (
                    <img src={img} alt={c.campaignName || ''} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${gradient}`} />
                  )}
                </div>
                <div className="p-2.5">
                  <p className="text-xs font-semibold text-gray-900 line-clamp-1">{c.campaignName || t('publicDashboard.campaign')}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{c.brandName || ''}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );

  /* ═══════════════════════════════════════════
     PAGINATION
     ═══════════════════════════════════════════ */
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-3 mt-8">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          이전
        </button>
        <span className="text-sm text-gray-500">
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          다음
        </button>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     LOADING SKELETON
     ═══════════════════════════════════════════ */
  const renderSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="h-40 bg-gray-200 animate-pulse" />
          <div className="p-3.5 space-y-2">
            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse mt-3" />
          </div>
        </div>
      ))}
    </div>
  );

  /* ═══════════════════════════════════════════
     EMPTY STATE
     ═══════════════════════════════════════════ */
  const renderEmpty = () => (
    <div className="text-center py-16">
      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <p className="text-gray-400 text-sm">{t('publicDashboard.noResults')}</p>
      <p className="text-gray-300 text-xs mt-1">{t('publicDashboard.noResultsHint')}</p>
    </div>
  );

  /* ═══════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />
      {renderCategoryTabs()}

      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Left sidebar */}
          {renderSidebar()}

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {renderViewToggle()}

            {loading ? (
              renderSkeleton()
            ) : paginated.length === 0 ? (
              renderEmpty()
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginated.map((c, i) => (
                  <CampaignGridCard key={c.campaignId} campaign={c} index={i} />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {paginated.map((c, i) => renderListRow(c, i))}
              </div>
            )}

            {renderPagination()}
          </div>

          {/* Right recommended widget */}
          {renderRecommended()}
        </div>
      </div>
    </div>
  );
};

export default PublicDashboardV2;
